import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { IconComponent } from '@ui';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators, } from '@angular/forms';
import { RemoteConfigService } from '../../../../app/services/remote-config.service';
import { Line, LvPanelConfiguration, LvPanelIdentifier, OutputConfiguration, } from '@data';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { PermissionsService } from '../../../../app/core/services/permissions.service';

interface OutputConfig {
  id: number;
  name: string;
  enabled: boolean;
  lineId: string;
  lineName: string;
  sabtCardId: string;
}

@Component({
  selector: 'ui-sabt-configuration-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzButtonModule,
    NzDrawerModule,
    NzStepsModule,
    NzFormModule,
    NzInputModule,
    NzSwitchModule,
    NzDividerModule,
    NzCollapseModule,
    NzInputNumberModule,
    NzSelectModule,
    NzIconModule,
    TranslocoDirective,
    IconComponent,
    ReactiveFormsModule,
    FormsModule,
  ],
  styles: [
    `
      .borderless-input {
        border: none;
        border-radius: 0;
        box-shadow: none;
        background-color: transparent;
      }
      .borderless-input:focus,
      .borderless-input:hover {
        border: none;
        box-shadow: none;
        background-color: transparent;
      }
      .form-container {
        border: 1px solid #e9ebf0;
        border-radius: 8px;
      }
      .form-container .ant-form-item {
        border-bottom: 1px solid #e9ebf0;
        margin-bottom: 0;
      }
      .form-container .ant-form-item:last-child {
        border-bottom: none;
      }
      .form-container .ant-form-item-label {
        border-right: 1px solid #e9ebf0;
      }
      .ant-form-item-control,
      .ant-form-item-label {
        padding-inline: 8px;
      }
      .form-container .ant-form-item-control:has(.ant-select) {
        padding-inline: 0;
      }
      .ant-input {
        padding: 0;
      }
      .blue-text {
        color: #1890ff;
      }

      .grid-control {
        overflow: hidden;
      }
      .input-disabled {
        background-color: #f5f5f5 !important;
      }
      .form-container .ant-select-selector {
        padding: 0;
      }
      .ant-select.borderless-input .ant-select-selector {
        border: none !important;
        box-shadow: none !important;
        background-color: transparent !important;
      }
      .ant-select.borderless-input .ant-select-arrow {
        right: 8px;
      }
      .collapse-content-padding {
        padding: 8px;
      }
      .collapse-grid {
        border: 1px solid #e9ebf0;
        border-radius: 8px;
      }
      .collapse-grid .grid-item {
        border-bottom: 1px solid #e9ebf0;
        margin-bottom: 0;
      }
      .collapse-grid .grid-item:last-child {
        border-bottom: none;
      }
      .collapse-grid .grid-label {
        border-right: 1px solid #e9ebf0;
        padding-inline: 8px;
        min-width: 266px;
      }
      .grid-control > input {
        padding: 4px 11px;
      }
      .lines-controller {
        display: flex;
        align-items: center;
        border: 1px solid #e9ebf0;
        border-radius: 8px;
      }
      .lines-controller > label {
        padding: 8px;
        border-right: 1px solid #e9ebf0;
      }
      .lines-controller > input {
        padding-inline: 8px;
      }
    `,
  ],
  template: `
    <div *transloco="let t">
      <nz-drawer
        [nzVisible]="visible()"
        [nzClosable]="false"
        nzPlacement="right"
        [nzTitle]="extra"
        [nzWidth]="'50%'"
        nzMaskClosable="false"
        (nzOnClose)="close()"
      >
        <ng-container *nzDrawerContent>
          <div class="flex flex-col h-full">
            <!-- Main content area -->
            <div class="flex-1 flex flex-col h-full">
              <!-- Stepper -->
              <nz-steps
                nzType="navigation"
                [nzCurrent]="currentStep()"
                (nzIndexChange)="onStepChange($event)"
              >
                <nz-step
                  [nzTitle]="title_step_1"
                  [nzStatus]="
                    currentStep() === 0
                      ? 'process'
                      : currentStep() > 0
                        ? 'finish'
                        : 'wait'
                  "
                ></nz-step>
                <nz-step
                  [nzTitle]="title_step_2"
                  [nzStatus]="
                    !isStep2Enabled()
                      ? 'wait'
                      : currentStep() === 1
                        ? 'process'
                        : currentStep() > 1
                          ? 'finish'
                          : 'wait'
                  "
                  [nzDisabled]="!isStep2Enabled()"
                >
                </nz-step>
              </nz-steps>

              <ng-template #title_step_1>
                {{ t('DISTRIBUTION_NETWORK.CONFIGURATION.STEP_1') }}
              </ng-template>
              <ng-template #title_step_2>
                {{ t('DISTRIBUTION_NETWORK.CONFIGURATION.STEP_2') }}
              </ng-template>

              <!-- Content based on current step -->
              <div class="overflow-auto h-full">
                @if (currentStep() === 0) {
                  <!-- General Data Step -->
                  <div>
                    <nz-divider
                      nzPlain
                      nzOrientation="left"
                      [nzText]="titleTemplate"
                    ></nz-divider>
                    <ng-template #titleTemplate>
                      <span class="text-sm font-bold">{{
                        t('DISTRIBUTION_NETWORK.CONFIGURATION.ENTITIES')
                      }}</span>
                    </ng-template>
                    <ng-template #requiredField>
                      {{
                        t('DISTRIBUTION_NETWORK.CONFIGURATION.REQUIRED_FIELD')
                      }}
                    </ng-template>
                    <ng-template #selectIdentifier>
                      {{
                        t(
                          'DISTRIBUTION_NETWORK.CONFIGURATION.SELECT_IDENTIFIER'
                        )
                      }}
                    </ng-template>
                    <ng-template #searchLine>
                      {{ t('DISTRIBUTION_NETWORK.CONFIGURATION.SEARCH_LINE') }}
                    </ng-template>
                    <ng-template #notFoundContent>
                      {{
                        t(
                          'DISTRIBUTION_NETWORK.CONFIGURATION.NOT_FOUND_CONTENT'
                        )
                      }}
                    </ng-template>
                    <form
                      nz-form
                      [formGroup]="generalDataForm"
                      class="form-container"
                    >
                      <!-- Distribuidora -->
                      <nz-form-item class="mb-0">
                        <nz-form-label
                          class="min-w-[266px]"
                          [nzSm]="8"
                          [nzXs]="24"
                          nzRequired
                          [nzLabelAlign]="'left'"
                          >{{
                            t('DISTRIBUTION_NETWORK.CONFIGURATION.DISTRIBUTOR')
                          }}</nz-form-label
                        >
                        <nz-form-control
                          class="input-disabled"
                          [nzSm]="16"
                          [nzXs]="24"
                          [nzErrorTip]="requiredField"
                        >
                          <input
                            nz-input
                            nzBorderless
                            class="borderless-input blue-text"
                            formControlName="distributorName"
                            [disabled]="true"
                          />
                        </nz-form-control>
                      </nz-form-item>

                      <!-- Center where it is located -->
                      <nz-form-item class="mb-0">
                        <nz-form-label
                          class="min-w-[266px]"
                          [nzSm]="8"
                          [nzXs]="24"
                          nzRequired
                          [nzLabelAlign]="'left'"
                          >{{
                            t('DISTRIBUTION_NETWORK.CONFIGURATION.CENTER')
                          }}</nz-form-label
                        >
                        <nz-form-control
                          class="input-disabled"
                          [nzSm]="16"
                          [nzXs]="24"
                          [nzErrorTip]="requiredField"
                        >
                          <input
                            nz-input
                            nzBorderless
                            class="borderless-input blue-text"
                            formControlName="centerLocation"
                            [disabled]="true"
                          />
                        </nz-form-control>
                      </nz-form-item>

                      <!-- Transformer identifier -->
                      <nz-form-item class="mb-0">
                        <nz-form-label
                          class="min-w-[266px]"
                          [nzSm]="8"
                          [nzXs]="24"
                          nzRequired
                          [nzLabelAlign]="'left'"
                          >{{
                            t('DISTRIBUTION_NETWORK.CONFIGURATION.TRANSFORMER')
                          }}</nz-form-label
                        >
                        <nz-form-control
                          class="input-disabled"
                          [nzSm]="16"
                          [nzXs]="24"
                          [nzErrorTip]="requiredField"
                        >
                          <input
                            nz-input
                            nzBorderless
                            class="borderless-input blue-text"
                            formControlName="transformerIdentifier"
                            [disabled]="true"
                          />
                        </nz-form-control>
                      </nz-form-item>

                      <!-- LV panel identifier -->
                      <nz-form-item class="mb-0">
                        <nz-form-label
                          class="min-w-[266px]"
                          [nzSm]="8"
                          [nzXs]="24"
                          nzRequired
                          [nzLabelAlign]="'left'"
                          >{{
                            t('DISTRIBUTION_NETWORK.CONFIGURATION.BOX')
                          }}</nz-form-label
                        >
                        <nz-form-control
                          [nzSm]="16"
                          [nzXs]="24"
                          [nzErrorTip]="requiredField"
                        >
                          <nz-select
                            nzShowSearch
                            formControlName="lvPanelIdentifier"
                            nzBorderless
                            class="w-full"
                            [nzPlaceHolder]="selectIdentifier"
                            (ngModelChange)="onLvPanelIdentifierChange($event)"
                          >
                            @for (
                              identifier of lvPanelIdentifiers();
                              track identifier.id
                            ) {
                              <nz-option
                                [nzValue]="identifier.id"
                                [nzLabel]="
                                  identifier.id + ' - ' + identifier.name
                                "
                              ></nz-option>
                            }
                          </nz-select>
                        </nz-form-control>
                      </nz-form-item>

                      <!-- Advanced low voltage supervisor -->
                      <nz-form-item class="mb-0">
                        <nz-form-label
                          class="min-w-[266px]"
                          [nzSm]="8"
                          [nzXs]="24"
                          [nzLabelAlign]="'left'"
                          >{{
                            t(
                              'DISTRIBUTION_NETWORK.CONFIGURATION.VOLTAGE_SUPERVISOR'
                            )
                          }}</nz-form-label
                        >
                        <nz-form-control [nzSm]="16" [nzXs]="24">
                          <nz-switch
                            formControlName="advancedLvSupervisor"
                            [nzCheckedChildren]="checkedTemplate"
                            [nzUnCheckedChildren]="uncheckedTemplate"
                            (ngModelChange)="
                              onAdvancedLvSupervisorChange($event)
                            "
                          ></nz-switch>
                        </nz-form-control>
                      </nz-form-item>

                      @if (generalDataForm.get('advancedLvSupervisor')?.value) {
                        <!-- Web address -->
                        <nz-form-item class="mb-0">
                          <nz-form-label
                            class="min-w-[266px]"
                            [nzSm]="8"
                            [nzXs]="24"
                            [nzLabelAlign]="'left'"
                            >{{
                              t('DISTRIBUTION_NETWORK.CONFIGURATION.ADDRESS')
                            }}</nz-form-label
                          >
                          <nz-form-control [nzSm]="16" [nzXs]="24">
                            <input
                              nz-input
                              nzBorderless
                              class="borderless-input"
                              formControlName="webAddress"
                              placeholder=""
                            />
                          </nz-form-control>
                        </nz-form-item>

                        <!-- SABT remote identifier -->
                        <nz-form-item class="mb-0">
                          <nz-form-label
                            class="min-w-[266px]"
                            [nzSm]="8"
                            [nzXs]="24"
                            [nzRequired]="isSabtRemoteIdentifierRequired()"
                            [nzLabelAlign]="'left'"
                            >{{
                              t('DISTRIBUTION_NETWORK.CONFIGURATION.ID_SABT')
                            }}</nz-form-label
                          >
                          <nz-form-control
                            [nzSm]="16"
                            [nzXs]="24"
                            [nzErrorTip]="requiredField"
                          >
                            <input
                              nz-input
                              nzBorderless
                              class="borderless-input"
                              formControlName="sabtRemoteIdentifier"
                              placeholder=""
                            />
                          </nz-form-control>
                        </nz-form-item>

                        <!-- VTN identifier -->
                        <nz-form-item class="mb-0">
                          <nz-form-label
                            class="min-w-[266px]"
                            [nzSm]="8"
                            [nzXs]="24"
                            [nzLabelAlign]="'left'"
                            >{{
                              t('DISTRIBUTION_NETWORK.CONFIGURATION.ID_VTN')
                            }}</nz-form-label
                          >
                          <nz-form-control [nzSm]="16" [nzXs]="24">
                            <input
                              nz-input
                              nzBorderless
                              class="borderless-input"
                              formControlName="vtnIdentifier"
                              placeholder=""
                            />
                          </nz-form-control>
                        </nz-form-item>
                      }
                    </form>
                  </div>
                }

                @if (currentStep() === 1) {
                  <!-- Outputs Step -->
                  <div>
                    <!-- Number of outputs -->
                    <div class="lines-controller mb-4 mt-4">
                      <label
                        for="totalOutputs"
                        class="block text-sm font-medium min-w-[266px]"
                        >{{
                          t('DISTRIBUTION_NETWORK.CONFIGURATION.OUTPUTS_NUMBER')
                        }}</label
                      >
                      <input
                        nz-input
                        nzBorderless
                        class="borderless-input blue-text"
                        id="totalOutputs"
                        [ngModel]="totalOutputs"
                        (blur)="onTotalOutputsChange($event)"
                        (keydown.enter)="onTotalOutputsChange($event)"
                        type="number"
                        min="0"
                        max="16"
                        placeholder=""
                      />
                    </div>

                    <!-- Outputs list -->
                    <nz-collapse
                      [nzBordered]="true"
                      [nzExpandIconPosition]="'end'"
                      [style]="customCollapseStyle"
                    >
                      @for (output of outputs(); track output.id) {
                        <nz-collapse-panel
                          [nzHeader]="outputHeaderTemplate"
                          [nzActive]="output.enabled"
                          [nzDisabled]="false"
                        >
                          <ng-template #outputHeaderTemplate>
                            <div class="flex items-center w-full">
                              <nz-switch
                                [(ngModel)]="output.enabled"
                                [nzCheckedChildren]="checkedTemplate"
                                [nzUnCheckedChildren]="uncheckedTemplate"
                                (click)="$event.stopPropagation()"
                                class="mr-3"
                              >
                              </nz-switch>
                              <span class="font-medium">{{ output.name }}</span>
                            </div>
                          </ng-template>

                          <!-- Collapse content -->
                          <div class="collapse-content-padding">
                            <div class="collapse-grid">
                              <!-- Row 1: Line -->
                              <div class="grid-item flex">
                                <label
                                  class="grid-label flex items-center text-sm font-medium"
                                  >{{
                                    t('DISTRIBUTION_NETWORK.CONFIGURATION.LINE')
                                  }}</label
                                >
                                <div
                                  class="grid-control flex-1 flex items-center"
                                >
                                  <nz-select
                                    nzShowSearch
                                    nzAllowClear
                                    [nzPlaceHolder]="t('ADMIN.SEARCH_LINE')"
                                    nzBorderless
                                    class="w-full borderless-input"
                                    [(ngModel)]="output.lineId"
                                    [nzNotFoundContent]="
                                      t('ADMIN.NOT_FOUND_CONTENT')
                                    "
                                    (nzOnSearch)="onLineSearch($event)"
                                    (ngModelChange)="
                                      onLineSelectionChange($event, output)
                                    "
                                    (click)="onLineDropdownClick()"
                                  >
                                    @for (
                                      line of filteredLines();
                                      track line.id
                                    ) {
                                      <nz-option
                                        [nzValue]="line.id"
                                        [nzLabel]="line.id + ' - ' + line.name"
                                      ></nz-option>
                                    }
                                  </nz-select>
                                </div>
                              </div>

                              <!-- Row 2: SABT Card Identifier -->
                              <div class="grid-item flex">
                                <label
                                  class="grid-label flex items-center text-sm font-medium"
                                  >{{
                                    t(
                                      'DISTRIBUTION_NETWORK.CONFIGURATION.ID_CARD_SABT'
                                    )
                                  }}</label
                                >
                                <div
                                  class="grid-control flex-1 flex items-center"
                                >
                                  <input
                                    nz-input
                                    nzBorderless
                                    class="borderless-input w-full"
                                    [(ngModel)]="output.sabtCardId"
                                    [placeholder]="
                                      t('ADMIN.ADD_CARD_IDENTIFIER')
                                    "
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </nz-collapse-panel>
                      }
                    </nz-collapse>
                  </div>
                }
              </div>

              <!-- Action buttons at bottom -->
              @if (hasWritePermission()) {
                <div class="flex justify-end gap-2 mt-auto pt-4">
                  <button nz-button nzType="default" (click)="onCancel()">
                    {{ t('DISTRIBUTION_NETWORK.CONFIGURATION.BTN_CANCEL') }}
                  </button>
                  <button
                    nz-button
                    nzType="primary"
                    [disabled]="!canSave()"
                    (click)="onSave()"
                  >
                    {{ t('DISTRIBUTION_NETWORK.CONFIGURATION.BTN_SAVE') }}
                  </button>
                </div>
              }
            </div>

            <!-- Shared templates for switches -->
            <ng-template #checkedTemplate>
              <ui-icon
                [icon]="'check'"
                style="color: white; font-size: 12px;"
              ></ui-icon>
            </ng-template>
            <ng-template #uncheckedTemplate>
              <ui-icon
                [icon]="'close'"
                style="color: white; font-size: 12px;"
              ></ui-icon>
            </ng-template>
          </div>
        </ng-container>

        <ng-template #extra>
          <div class="flex items-center justify-between w-full">
            <span class="text-lg font-semibold">{{
              t('DISTRIBUTION_NETWORK.CONFIGURATION.TITLE')
            }}</span>
            <button
              nz-button
              nzType="text"
              class="flex items-center justify-center w-8 h-8 p-0"
              (click)="close()"
            >
              <ui-icon
                [icon]="'close'"
                [theme]="'outline'"
                style="color: #262626; font-size: 16px;"
              />
            </button>
          </div>
        </ng-template>
      </nz-drawer>
    </div>
  `,
})
export class SabtConfigurationDrawerComponent implements OnInit {
  private readonly DEFAULT_TOTAL_OUTPUTS = 6;

  private fb = inject(FormBuilder);
  private remoteConfigService = inject(RemoteConfigService);
  private destroyRef = inject(DestroyRef);
  private modal = inject(NzModalService);
  private notification = inject(NzNotificationService);
  private transloco = inject(TranslocoService);
  protected readonly permissionsService = inject(PermissionsService);

  visible = input<boolean>(false);
  rowData = input<any>(null);
  closeDrawer = output<void>();
  permissions = this.permissionsService.permissions;

  // Stepper
  currentStep = signal<number>(0);

  // Form
  generalDataForm!: FormGroup;
  totalOutputs = this.DEFAULT_TOTAL_OUTPUTS;
  outputs = signal<OutputConfig[]>([]);

  // Remote Config Data
  lvPanelIdentifiers = signal<LvPanelIdentifier[]>([]);
  lvPanelConfiguration = signal<LvPanelConfiguration | null>(null);
  availableLines = signal<Line[]>([]);
  filteredLines = signal<Line[]>([]);
  allLinesFetched = signal<boolean>(false);

  // Store current row data for API calls
  private currentRowData: any = null;

  // Memory array to persist all output data (up to 16)
  private outputsMemory: OutputConfig[] = [];

  // Collapse style
  readonly customCollapseStyle = {
    background: 'transparent',
    border: '1px solid #E9EBF0',
    'border-radius': '8px 8px 0 0',
    'margin-bottom': '0px',
  };

  // Effects at class level (constructor context)
  constructor() {
    // Main effect to populate form when both conditions are met
    effect(() => {
      const visible = this.visible();
      const data = this.rowData();

      if (visible && data) {
        // Reset state when drawer opens
        this.resetDrawerState();
        this.populateFormWithRowData();
      }
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeOutputs();
  }

  /**
   * Returns true if the user has 'w' write permission.
   */
  hasWritePermission(): boolean {
    return this.permissions().some((p) => p === 'sabt:alarms:w');
  }

  /**
   * Returns true if the form is valid and can be saved.
   */
  canSave(): boolean {
    return this.generalDataForm.valid;
  }

  /**
   * Returns true if the sabtRemoteIdentifier field should be required.
   */
  isSabtRemoteIdentifierRequired(): boolean {
    const lvPanelIdentifier =
      this.generalDataForm.get('lvPanelIdentifier')?.value;
    const advancedLvSupervisor = this.generalDataForm.get(
      'advancedLvSupervisor',
    )?.value;
    return !!(lvPanelIdentifier && advancedLvSupervisor);
  }

  private populateFormWithRowData(): void {
    const data = this.rowData();
    if (data) {
      // Store current row data for API calls
      this.currentRowData = data;

      // Update form with the 3 specific fields from row data
      // Use patchValue with emitEvent: false for disabled controls
      this.generalDataForm.patchValue(
        {
          distributorName: data.distributorNameUser || '',
          centerLocation: data.center?.centerName || '',
          transformerIdentifier: data.transformer?.id || '',
        },
        { emitEvent: false },
      );

      // Alternatively, set values directly for disabled controls
      this.generalDataForm
        .get('distributorName')
        ?.setValue(data.distributorNameUser || '', { emitEvent: false });
      this.generalDataForm
        .get('centerLocation')
        ?.setValue(
          `${data.centerId || ''} - ${data.center?.centerName || ''}`,
          { emitEvent: false },
        );
      this.generalDataForm
        .get('transformerIdentifier')
        ?.setValue(
          `${data.transformer?.id || ''} - ${data.transformer?.name || ''}`,
          { emitEvent: false },
        );

      // Fetch lv panel identifiers if we have the required data
      const centerId = data.centerId;
      const transformerId = data.transformerId;
      const distributorId = data.distributorId || data.distributor?.id || '';

      if (centerId && transformerId && distributorId) {
        this.fetchLvPanelIdentifiers(distributorId, centerId, transformerId);
      }
    }
  }

  private resetDrawerState(): void {
    this.currentRowData = null;
    this.currentStep.set(0);
    this.totalOutputs = this.DEFAULT_TOTAL_OUTPUTS;
    this.allLinesFetched.set(false);

    if (this.generalDataForm) {
      this.generalDataForm.reset(
        {
          distributorName: '',
          centerLocation: '',
          transformerIdentifier: '',
          lvPanelIdentifier: '',
          advancedLvSupervisor: false,
          webAddress: '',
          sabtRemoteIdentifier: '',
          vtnIdentifier: '',
        },
        { emitEvent: false },
      );

      this.generalDataForm
        .get('distributorName')
        ?.disable({ emitEvent: false });
      this.generalDataForm.get('centerLocation')?.disable({ emitEvent: false });
      this.generalDataForm
        .get('transformerIdentifier')
        ?.disable({ emitEvent: false });
      this.generalDataForm.markAsPristine();
      this.generalDataForm.markAsUntouched();

      this.updateSabtRemoteIdentifierValidation();
    }

    this.lvPanelIdentifiers.set([]);
    this.lvPanelConfiguration.set(null);
    this.availableLines.set([]);
    this.filteredLines.set([]);
    this.outputsMemory = this.createEmptyOutputsMemory();
    this.outputs.set([]);
  }

  private fetchLvPanelIdentifiers(
    distributorId: string,
    centerId: string,
    transformerId: string,
  ): void {
    this.remoteConfigService
      .getLvPanelIdentifiers({
        distributorId,
        centerId,
        transformerId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (identifiers: LvPanelIdentifier[]) => {
          this.lvPanelIdentifiers.set(identifiers);
          this.generalDataForm
            .get('lvPanelIdentifier')
            ?.setValue(identifiers[0].id || '', { emitEvent: false });
        },
        error: (error) => {
          console.error('Error fetching lv panel identifiers:', error);
          this.lvPanelIdentifiers.set([]);
        },
      });
  }

  private fetchLvPanelConfiguration(lvPanelId: string): void {
    if (!this.currentRowData) return;

    const centerId = this.currentRowData.centerId;
    const transformerId = this.currentRowData.transformerId;
    const distributorId =
      this.currentRowData.distributorId ||
      this.currentRowData.distributor?.id ||
      '';

    if (!centerId || !transformerId || !distributorId) return;

    this.remoteConfigService
      .getLvPanelConfiguration({
        distributorId,
        lvPanelId,
        centerId,
        transformerId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (configuration: LvPanelConfiguration | null) => {
          if (configuration?.conflicts) {
            this.generalDataForm.disable();
          } else {
            this.generalDataForm.enable();
          }

          // Sort outputs by `order` ascending before applying the configuration
          if (configuration && Array.isArray(configuration.outputs)) {
            const sortedOutputs = [...configuration.outputs].sort(
              (a, b) =>
                (a.order ?? Number.POSITIVE_INFINITY) -
                (b.order ?? Number.POSITIVE_INFINITY),
            );
            // create new object to avoid mutating external references
            configuration = { ...configuration, outputs: sortedOutputs };
          }

          this.lvPanelConfiguration.set(configuration);
          if (configuration) {
            this.populateFormWithConfiguration(configuration);
          }
        },
        error: (error) => {
          console.error('Error fetching lv panel configuration:');
          console.error('- Error details:', error);
          console.error('- Error status:', error?.status);
          console.error('- Error message:', error?.message);
          console.error('- Error body:', error?.error);
          this.lvPanelConfiguration.set(null);
        },
      });
  }

  private populateFormWithConfiguration(config: LvPanelConfiguration): void {
    // Update the advancedLvSupervisor switch based on the sabt property
    this.generalDataForm.patchValue({
      advancedLvSupervisor: config.sabt,
      webAddress: config.addressWeb,
      sabtRemoteIdentifier: config.sabtId,
      vtnIdentifier: config.vtnId,
    });

    // Update validation after populating the form
    this.updateSabtRemoteIdentifierValidation();

    // Update the total outputs and outputs list
    this.totalOutputs = config.numOutputs;
    this.updateOutputsFromConfiguration(config.outputs);
  }

  private updateOutputsFromConfiguration(
    configOutputs: OutputConfiguration[],
  ): void {
    // Update the memory array with the configuration data
    configOutputs.forEach((configOutput, index) => {
      if (index < this.outputsMemory.length) {
        this.outputsMemory[index] = {
          id: configOutput.order,
          name: `${this.transloco.translate('ADMIN.OUTPUT')} ${configOutput.order}`,
          enabled: configOutput.active,
          lineId: configOutput.lineId,
          lineName: configOutput.lineName,
          sabtCardId: configOutput.lineSabt,
        };
      }
    });

    // Set initial available lines with just the configuration lines
    // All lines will be fetched when user navigates to step 2
    const configurationLines: Line[] = configOutputs.map((output) => ({
      id: output.lineId,
      name: output.lineName,
    }));
    this.availableLines.set(configurationLines);
    this.filteredLines.set(configurationLines);

    // Update visible outputs
    this.updateOutputsList(this.totalOutputs);
  }

  onLvPanelIdentifierChange(selectedId: string): void {
    if (selectedId) {
      this.fetchLvPanelConfiguration(selectedId);
      // Update sabtRemoteIdentifier validation based on current state
      this.updateSabtRemoteIdentifierValidation();
      // Optionally auto-advance to step 2 when a selection is made
      // Uncomment the next line if you want automatic step advancement
      // this.currentStep.set(1);
    } else {
      this.lvPanelConfiguration.set(null);
      // Remove required validator when no panel is selected
      this.generalDataForm.get('sabtRemoteIdentifier')?.clearValidators();
      this.generalDataForm
        .get('sabtRemoteIdentifier')
        ?.updateValueAndValidity();
      // If no selection, ensure we're on step 1 and block step 2
      if (this.currentStep() > 0) {
        this.currentStep.set(0);
      }
    }
  }

  private initializeForm(): void {
    this.generalDataForm = this.fb.group({
      distributorName: [{ value: '', disabled: true }, [Validators.required]],
      centerLocation: [{ value: '', disabled: true }, [Validators.required]],
      transformerIdentifier: [
        { value: '', disabled: true },
        [Validators.required],
      ],
      lvPanelIdentifier: ['', [Validators.required]],
      advancedLvSupervisor: [false],
      webAddress: [''],
      sabtRemoteIdentifier: [''],
      vtnIdentifier: [''],
    });
  }

  private initializeOutputs(): void {
    // Initialize memory array and visible outputs with empty data
    this.outputsMemory = this.createEmptyOutputsMemory();
    this.outputs.set([]);
  }

  onStepChange(index: number): void {
    // Only allow navigation to step 2 if LV panel identifier is selected
    if (index === 1 && !this.isStep2Enabled()) {
      return; // Block navigation to step 2
    }

    // Fetch all available lines when navigating to step 1 (outputs configuration)
    if (index === 1 && !this.allLinesFetched()) {
      this.fetchAllLinesForStep2();
    }

    this.currentStep.set(index);
  }

  isStep2Enabled(): boolean {
    const lvPanelIdentifier =
      this.generalDataForm.get('lvPanelIdentifier')?.value;
    return !!(lvPanelIdentifier && lvPanelIdentifier.trim() !== '');
  }

  onAdvancedLvSupervisorChange(isEnabled: boolean): void {
    const data = this.lvPanelConfiguration();
    if (!isEnabled) {
      // Clear the dependent fields when supervisor is turned off
      this.generalDataForm.patchValue({
        webAddress: '',
        sabtRemoteIdentifier: '',
        vtnIdentifier: '',
      });
    } else {
      // set the dependent fields when supervisor is turned on
      this.generalDataForm.patchValue({
        webAddress: data?.addressWeb || '',
        sabtRemoteIdentifier: data?.sabtId || '',
        vtnIdentifier: data?.vtnId || '',
      });
    }
    // Update sabtRemoteIdentifier validation based on current state
    this.updateSabtRemoteIdentifierValidation();
  }

  /**
   * Updates the validation for sabtRemoteIdentifier based on current form state.
   * Field is required when both lvPanelIdentifier is selected AND advancedLvSupervisor is enabled.
   */
  private updateSabtRemoteIdentifierValidation(): void {
    const lvPanelIdentifier =
      this.generalDataForm.get('lvPanelIdentifier')?.value;
    const advancedLvSupervisor = this.generalDataForm.get(
      'advancedLvSupervisor',
    )?.value;
    const sabtRemoteIdentifierControl = this.generalDataForm.get(
      'sabtRemoteIdentifier',
    );

    if (lvPanelIdentifier && advancedLvSupervisor) {
      // Make field required when both conditions are met
      sabtRemoteIdentifierControl?.setValidators([Validators.required]);
    } else {
      // Remove required validator when conditions are not met
      sabtRemoteIdentifierControl?.clearValidators();
    }

    sabtRemoteIdentifierControl?.updateValueAndValidity();
  }

  onTotalOutputsChange(event: any): void {
    const newTotal = parseInt(event.target.value, 10);

    // Ensure the value is within bounds
    if (isNaN(newTotal) || newTotal < 0) {
      event.target.value = 0;
      return;
    }
    if (newTotal > 16) {
      event.target.value = 16;
      return;
    }

    // Save current visible outputs back to memory before changing
    this.saveCurrentOutputsToMemory();

    this.totalOutputs = newTotal;
    this.updateOutputsList(newTotal);
  }

  private saveCurrentOutputsToMemory(): void {
    const currentOutputs = this.outputs();
    currentOutputs.forEach((output) => {
      const memoryIndex = output.id - 1;
      if (memoryIndex >= 0 && memoryIndex < this.outputsMemory.length) {
        this.outputsMemory[memoryIndex] = { ...output };
      }
    });
  }

  private updateOutputsList(newTotal: number): void {
    // Always get outputs from memory to preserve data
    const visibleOutputs = this.outputsMemory.slice(0, newTotal);
    this.outputs.set([...visibleOutputs]);
  }

  onLineSearch(searchValue: string): void {
    // Filter the lines based on the search value
    const allLines = this.availableLines();
    if (!searchValue || searchValue.trim() === '') {
      // If no search value, show all lines
      this.filteredLines.set(allLines);
    } else {
      // Filter lines that match the search term (case insensitive)
      const filtered = allLines.filter(
        (line) =>
          line.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          line.id.toLowerCase().includes(searchValue.toLowerCase()),
      );
      this.filteredLines.set(filtered);
    }
  }

  onLineSelectionChange(selectedLineId: string, output: OutputConfig): void {
    if (!selectedLineId) {
      this.onLineClear(output);
      return;
    }
    // Find the selected line and update the output
    const selectedLine = this.availableLines().find(
      (line) => line.id === selectedLineId,
    );

    if (selectedLine) {
      output.lineId = selectedLine.id;
      output.lineName = selectedLine.name;
    } else {
      output.lineId = selectedLineId || '';
      output.lineName = '';
    }

    // Update the memory as well
    const memoryIndex = output.id - 1;
    if (memoryIndex >= 0 && memoryIndex < this.outputsMemory.length) {
      this.outputsMemory[memoryIndex].lineId = output.lineId;
      this.outputsMemory[memoryIndex].lineName = output.lineName;
    }
  }

  onLineClear(output: OutputConfig): void {
    // Clear the line selection
    output.lineId = '';
    output.lineName = '';

    // Update the memory as well
    const memoryIndex = output.id - 1;
    if (memoryIndex >= 0 && memoryIndex < this.outputsMemory.length) {
      this.outputsMemory[memoryIndex].lineId = '';
      this.outputsMemory[memoryIndex].lineName = '';
    }
  }

  onLineDropdownClick(): void {
    // Ensure all lines are fetched (fallback if user clicked dropdown before navigating to step 2)
    this.fetchLinesForDropdown();
  }

  private fetchAllLinesForStep2(): void {
    this.remoteConfigService
      .getLines(this.rowData().distributorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (allLines: Line[]) => {
          // Combine configuration lines with all available lines
          const configurationLines = this.getConfigurationLines();
          const combinedLines = this.combineLines(configurationLines, allLines);

          this.availableLines.set(combinedLines);
          this.filteredLines.set(combinedLines);
          this.allLinesFetched.set(true);
        },
        error: (error) => {
          console.error('Error fetching all lines for step 2:', error);
          // Keep the configuration lines as fallback
          const configurationLines = this.getConfigurationLines();
          this.availableLines.set(configurationLines);
          this.filteredLines.set(configurationLines);
        },
      });
  }

  private fetchLinesForDropdown(): void {
    // This method is now only used as fallback if lines weren't fetched yet
    if (!this.allLinesFetched()) {
      this.fetchAllLinesForStep2();
    }
  }

  private getConfigurationLines(): Line[] {
    const config = this.lvPanelConfiguration();
    if (!config || !config.outputs) {
      return [];
    }

    return config.outputs.map((output) => ({
      id: output.lineId,
      name: output.lineName,
    }));
  }

  private combineLines(configurationLines: Line[], allLines: Line[]): Line[] {
    // Create a map to avoid duplicates (use line id as key)
    const linesMap = new Map<string, Line>();

    // First add configuration lines (these should appear first/prioritized)
    configurationLines.forEach((line) => {
      if (line.id && line.name) {
        linesMap.set(line.id, line);
      }
    });

    // Then add all other lines that aren't already in the configuration
    allLines.forEach((line) => {
      if (line.id && line.name && !linesMap.has(line.id)) {
        linesMap.set(line.id, line);
      }
    });

    return Array.from(linesMap.values());
  }

  private createEmptyOutputsMemory(): OutputConfig[] {
    return Array.from({ length: 16 }, (_, index) => {
      const id = index + 1;
      return {
        id,
        name: `${this.transloco.translate('ADMIN.OUTPUT')} ${id}`,
        enabled: false,
        lineId: '',
        lineName: '',
        sabtCardId: '',
      };
    });
  }

  close(): void {
    this.closeDrawer.emit();
  }

  onCancel(): void {
    this.close();
  }

  onSave(): void {
    // Show confirmation modal before saving
    this.modal.confirm({
      nzTitle: this.transloco.translate('ADMIN.MODAL_CONFIRM.TITLE_CONFIG'),
      nzContent: this.transloco.translate('ADMIN.MODAL_CONFIRM.CONTENT_CONFIG'),
      nzCentered: true,
      nzOkText: this.transloco.translate('ADMIN.MODAL_CONFIRM.OK'),
      nzCancelText: this.transloco.translate('ADMIN.MODAL_CONFIRM.CANCEL'),
      nzOnOk: () => {
        this.performSave();
      },
    });
  }

  private performSave(): void {
    if (!this.currentRowData) {
      console.error('No row data available for saving');
      return;
    }

    // Validate form
    if (this.generalDataForm.invalid) {
      console.error('Form is invalid');

      // Check specifically for sabtRemoteIdentifier validation
      const sabtRemoteIdentifierControl = this.generalDataForm.get(
        'sabtRemoteIdentifier',
      );
      if (sabtRemoteIdentifierControl?.hasError('required')) {
        this.notification.create(
          'error',
          this.transloco.translate('ADMIN.REQUIRED_FIELD'),
          this.transloco.translate('ADMIN.REQUIRED_FIELD_MESSAGE'),
          {
            nzPlacement: 'bottomRight',
          },
        );
        return;
      }

      // Generic validation error for other fields
      this.notification.create(
        'error',
        this.transloco.translate('ADMIN.INVALID_FORM'),
        this.transloco.translate('ADMIN.INVALID_FORM_MESSAGE'),
        {
          nzPlacement: 'bottomRight',
        },
      );
      return;
    }

    const formData = this.generalDataForm.value;
    const outputs = this.outputs();

    // Objeto en camelCase según el modelo actualizado
    const requestBody: any = {
      addressWeb: formData.webAddress || '',
      sabt: formData.advancedLvSupervisor || false,
      sabtId: formData.sabtRemoteIdentifier || '',
      vtnId: formData.vtnIdentifier || '',
      numOutputs: this.totalOutputs,
      outputs: outputs.map((output, index) => ({
        active: output.enabled,
        fuseSize: 0,
        fuseState: 0,
        lineId: output.lineId || '',
        lineName: output.lineName || '',
        lineSabt: output.sabtCardId || '',
        order: index + 1,
      })),
    };

    // Get the selected LV Panel identifier (activeID) for the API call
    const lvPanelId = this.generalDataForm.get('lvPanelIdentifier')?.value;

    if (!lvPanelId) {
      console.error('No LV Panel identifier selected for saving');
      return;
    }

    // Call the update service using the LV Panel ID as centerId parameter
    // Note: The centerId parameter in the API actually expects the LT Box activeID (e.g., 278.CBT.SG000617549)
    // not the Center ID (e.g., 278.CTR.XX00000021)
    const distributorId =
      this.currentRowData?.distributorId ||
      this.currentRowData?.distributor?.id ||
      '';

    if (!distributorId) {
      console.error('No distributorId available for saving configuration');
      return;
    }

    this.remoteConfigService
      .updateLvPanelConfiguration(
        { distributorId, centerId: lvPanelId },
        requestBody,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          console.log('=== SUCCESS CALLBACK TRIGGERED ===');
          console.log('Response:', response);
          console.log('Response type:', typeof response);
          console.log('Response is null?', response === null);
          console.log('Response is undefined?', response === undefined);

          // Only show success notification if we have a valid response
          // A null response indicates an error occurred (even if HTTP status was 200)
          if (response !== null && response !== undefined) {
            this.notification.create(
              'success',
              this.transloco.translate('ADMIN.CONFIG_REMOTA'),
              this.transloco.translate('ADMIN.CONFIG_REMOTA_SAVED'),
              {
                nzPlacement: 'bottomRight',
              },
            );
            this.close();
          } else {
            console.warn(
              'Received null/undefined response - likely an error occurred. Not showing success notification.',
            );
          }
        },
        error: (error) => {
          console.log('=== ERROR CALLBACK TRIGGERED ===');
          console.log('Error:', error);
          console.log('Error status:', error?.status);
          console.log('Error message:', error?.message);
          console.log('Error details:', error?.error);
          // Let the interceptor handle the error display
        },
      });
  }
}
